/**
 * Color utility functions for dynamic theming
 */

/**
 * Convert hex color to RGB values
 * @param {string} hex - Hex color string (e.g., '#ff0000' or 'ff0000')
 * @returns {object} RGB values {r, g, b}
 */
export const hexToRgb = (hex) => {
  // Remove # if present
  hex = hex.replace('#', '');

  // Handle 3-digit hex codes
  if (hex.length === 3) {
    hex = hex.split('').map(char => char + char).join('');
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return { r, g, b };
};

/**
 * Convert RGB values to hex color
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {string} Hex color string
 */
export const rgbToHex = (r, g, b) => {
  const toHex = (n) => {
    const hex = Math.round(Math.max(0, Math.min(255, n))).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Darken a color by a percentage
 * @param {string} color - Hex color string
 * @param {number} percentage - Percentage to darken (0-100)
 * @returns {string} Darkened hex color
 */
export const darkenColor = (color, percentage) => {
  if (!color) return color;

  const { r, g, b } = hexToRgb(color);
  const factor = 1 - (percentage / 100);

  return rgbToHex(
    Math.round(r * factor),
    Math.round(g * factor),
    Math.round(b * factor)
  );
};

/**
 * Lighten a color by a percentage
 * @param {string} color - Hex color string
 * @param {number} percentage - Percentage to lighten (0-100)
 * @returns {string} Lightened hex color
 */
export const lightenColor = (color, percentage) => {
  if (!color) return color;

  const { r, g, b } = hexToRgb(color);
  const factor = percentage / 100;

  return rgbToHex(
    Math.round(r + (255 - r) * factor),
    Math.round(g + (255 - g) * factor),
    Math.round(b + (255 - b) * factor)
  );
};

/**
 * Convert hex color to rgba with opacity
 * @param {string} color - Hex color string
 * @param {number} opacity - Opacity value (0-1)
 * @returns {string} RGBA color string
 */
export const hexToRgba = (color, opacity) => {
  if (!color) return `rgba(0, 0, 0, ${opacity})`;

  const { r, g, b } = hexToRgb(color);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Get hover color based on primary color
 * @param {string} primaryColor - Primary color hex string
 * @param {string} type - Type of hover effect ('darken', 'lighten', 'opacity')
 * @param {number} intensity - Intensity of the effect (default: 10 for darken/lighten, 0.08 for opacity)
 * @returns {string} Hover color
 */
export const getHoverColor = (primaryColor, type = 'darken', intensity) => {
  if (!primaryColor) {
    // Fallback colors
    switch (type) {
      case 'darken':
        return '#5a67d8';
      case 'lighten':
        return '#f7fafc';
      case 'opacity':
        return 'rgba(102, 126, 234, 0.08)';
      default:
        return primaryColor;
    }
  }

  switch (type) {
    case 'darken':
      return darkenColor(primaryColor, intensity || 15);
    case 'lighten':
      return lightenColor(primaryColor, intensity || 10);
    case 'opacity':
      return hexToRgba(primaryColor, intensity || 0.08);
    default:
      return primaryColor;
  }
};
